/**
 * ScrollingText -- shared component for text that may overflow its container.
 *
 * By default, overflowing text shows a static ellipsis and animates marquee-style
 * on hover. Callers can override hover detection via `isHovered` (e.g. when the
 * hover trigger is a parent card, not the text itself).
 *
 * Animation uses the `animate-scroll-text` CSS class (defined in styles/index.css),
 * which respects `prefers-reduced-motion`.
 *
 * Text color is NOT set here — callers provide it via `className`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../utils/cn.js";

export interface ScrollingTextProps {
  text: string;
  /** Override hover detection from a parent element. When omitted, the component tracks its own hover. */
  isHovered?: boolean;
  className?: string;
}

export const ScrollingText = ({ text, isHovered, className }: ScrollingTextProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [scrollDistance, setScrollDistance] = useState(0);
  const [selfHovered, setSelfHovered] = useState(false);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (container && textEl) {
      const diff = textEl.scrollWidth - container.clientWidth;
      setOverflows(diff > 1);
      setScrollDistance(Math.max(0, diff));
    }
  }, []);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [measure, text]);

  const hovered = isHovered ?? selfHovered;
  const shouldAnimate = overflows && hovered;

  // Constant scroll speed: 30px/s so all text moves at the same rate
  const duration = scrollDistance > 0 ? Math.max(2, scrollDistance / 30) : 3.5;

  return (
    <div
      ref={containerRef}
      className="max-w-full overflow-hidden whitespace-nowrap"
      onMouseEnter={isHovered === undefined ? () => setSelfHovered(true) : undefined}
      onMouseLeave={isHovered === undefined ? () => setSelfHovered(false) : undefined}
    >
      <span
        ref={textRef}
        className={cn(
          "inline-block font-medium leading-tight",
          className,
          overflows && !shouldAnimate && "truncate-inline",
          shouldAnimate && "animate-scroll-text",
        )}
        style={
          shouldAnimate
            ? ({
                "--scroll-distance": `${scrollDistance}px`,
                "--scroll-duration": `${duration.toFixed(1)}s`,
              } as React.CSSProperties)
            : undefined
        }
      >
        {text}
      </span>
    </div>
  );
};
